import React from 'react';
import { t } from 'i18n';
import swiftxLogo from '../images/swiftx-logo.png';
import { generateBarcodeDataUrl } from '../../../../../../utils/BarCodeUtil';
import { BOL3_STYLES } from './ts/bolBaseStyles';
import { BagDetail, BolDataV3, BolStop, StationLoadingDetail } from './types/bolTypes';
import { escapeHtml, getAbbrSortCode, isHandoverStyleBol, sealFromSortCode } from './ts/bolUtils';
import { TmsTaskTypeEnum } from '../../../../../../enums/TmsEnum';

/**
 * 站点标题：index 0 -> SHIP FROM；最后一站 -> SHIP TO；中间 -> 1ST/2ND/3RD STOP
 */
const stopHeading = (index: number, stopCount: number): string => {
    if (index === 0) return 'SHIP FROM';
    if (index === stopCount - 1) return 'SHIP TO';
    const ordinals = ['1ST', '2ND', '3RD'];
    return `${ordinals[index - 1] ?? `${index}TH`} STOP`;
};

/** 每个 stop 的站点代码（stops[i].shipTo* 即该站信息，与 v2 语义一致） */
const stopCity = (stop: BolStop): string => getAbbrSortCode(stop.shipToSortCode);

/**
 * 时间串截取到分钟（"HH:mm:ss" -> "HH:mm"），非法格式返回空串
 */
const formatTimeToHHmm = (time?: string | null) => {
    if (!time) return '';
    const segments = time.split(':');
    if (!segments[0] || !segments[1]) return '';
    return `${segments[0]}:${segments[1]}`;
};

/**
 * 格式化「Static ETA」（发车后级联计算的静态预计到达，非计划到达时间）：
 * 无值时返回空串；有值时展示「时间 日期 时区」（单空格分隔，配合 nowrap 保持单行）
 */
const formatStaticEta = (stop: BolStop): string => {
    if (!stop.staticEtaDate && !stop.staticEtaTime) return '';
    const time = formatTimeToHHmm(stop.staticEtaTime);
    return [time, stop.staticEtaDate || '', stop.staticEtaLocalTimezone || '']
        .filter(Boolean)
        .join(' ');
};

/** 重量格式化：保留两位小数，空值返回空串 */
const formatWeight = (weight?: number | null): string => {
    if (weight == null) return '';
    return weight.toFixed(2);
};

/**
 * Header：标题 + logo
 */
const headerEle = () => `
    <div class="bol3-header">
        <div class="bol3-title">BILL OF LADING</div>
        <div class="bol3-logo" role="img" aria-label="SwiftX" style="background-image:url('${swiftxLogo}')"></div>
    </div>
`;

/**
 * Date/Route + BOL 条码区
 */
const metaEle = (bolData: BolDataV3) => {
    // 路线名 "LA-HOU-DFW" -> "LA — HOU — DFW"
    const routeText = (bolData.routeName || '').replace(/\s*-\s*/g, ' — ').trim();
    // 三方任务（TT使用外部运单号）
    const isThirdParty = bolData.taskType === TmsTaskTypeEnum.ThirdParty;
    const documentNumber = isThirdParty
        ? bolData.freightOrderId
        : bolData.billOfLadingNumber;
    const barcodeDataUrl = generateBarcodeDataUrl(documentNumber);
    const barcodeStyle = barcodeDataUrl ? ` style="background-image:url('${barcodeDataUrl}')"` : '';
    // 三方任务条码下是外部运单号，加括号与下方 BOL 号区分
    const documentNumberHtml = isThirdParty
        ? `(${escapeHtml(documentNumber)})`
        : escapeHtml(documentNumber);
    // 三方任务条码区展示的是外部运单号，BOL 号在其下方另起一行补充
    const bolNumberHtml = isThirdParty && bolData.billOfLadingNumber
        ? `<div class="bol3-bol-number bol3-bol-id">
               <span class="bol3-k">BOL Number：</span>
               <span class="bol3-v bol3-mono">${escapeHtml(bolData.billOfLadingNumber)}</span>
           </div>`
        : '';
    return `
        <table class="bol3-block-table bol3-meta-table">
            <tbody>
                <tr>
                    <td class="bol3-meta-left" style="width:50%;">
                        <div class="bol3-kv">
                            <span class="bol3-k">Date</span>
                            <span class="bol3-v bol3-mono">${escapeHtml(bolData.date)}</span>
                        </div>
                        <div class="bol3-kv">
                            <span class="bol3-k">Route</span>
                            <span class="bol3-v">${escapeHtml(routeText)}${bolData.routeCode ? ` (${escapeHtml(bolData.routeCode)})` : ''}</span>
                        </div>
                    </td>
                    <td class="bol3-meta-right" style="width:50%;">
                        <div class="bol3-barcode"${barcodeStyle}></div>
                        <div class="bol3-bol-number">
                            <span class="bol3-v bol3-mono">${documentNumberHtml}</span>
                        </div>
                        ${bolNumberHtml}
                    </td>
                </tr>
            </tbody>
        </table>
    `;
};

/** 联系人行：姓名 · 电话；缺一项只展示有的那项，都没有则留空 */
const contactLineHtml = (name?: string | null, phone?: string | null): string => {
    const nameHtml = name ? `<span class="bol3-name">${escapeHtml(name)}</span>` : '';
    const phoneHtml = phone ? `<span class="bol3-phone bol3-mono">${escapeHtml(phone)}</span>` : '';
    return [nameHtml, phoneHtml].filter(Boolean).join(' · ');
};

/** 单个站点信息卡片（table 单元格，列宽由调用方按列数均分传入） */
const shipCellEle = (heading: string, addressLine: string, contactHtml: string, width: string): string => `
        <td class="bol3-ship-cell" style="width:${width};">
            <div class="bol3-ship-label">${heading}</div>
            <div class="bol3-ship-address">${addressLine}</div>
            <div class="bol3-ship-contact">${contactHtml}</div>
        </td>
`;

/**
 * 站点信息区：
 * - 普通样式：Ship From / N STOP / Ship To 多列（列数随 stops 动态）；非交接任务与三方任务（三方按 shipmentPlan 分多段）走这套
 * - 交接样式：固定两列 SHIP FROM + HANDOVER CARRIER（交接方名称显示在地址位置，下方为联系人电话）
 */
const shipInfoEle = (bolData: BolDataV3) => {
    const stopCount = bolData.stops.length;
    const isHandoverStyle = isHandoverStyleBol(bolData);

    // 街道地址与「城市,州,邮编」默认分两行展示；交接任务只有两列、空间充足，合并成一行
    // 任一项为空时不留空行 / 不留多余分隔符
    const addressLines = (stop: BolStop, singleLine = false) => [
        escapeHtml(stop.shipToAddress),
        escapeHtml(stop.shipToCityStateZipcode)
    ].filter(Boolean).join(singleLine ? ' ' : '<br/>');

    const stopCell = (stop: BolStop, heading: string, width: string, singleLineAddress = false) => shipCellEle(
        heading,
        addressLines(stop, singleLineAddress),
        contactLineHtml(stop.shipToContactName, stop.shipToContactPhoneNumber),
        width
    );

    // 列宽按列数均分（table-layout:fixed 需要显式列宽，不能靠 1fr）
    const columnWidth = (count: number) => `${(100 / count).toFixed(4)}%`;

    // 标题「SHIP FROM — LAX」拆成两个 span 便于分别设样式：
    // bol3-ship-stop = 站点名 + 破折号，bol3-ship-code = 站点代码；无代码时不留下孤零零的破折号
    const headingHtml = (label: string, code: string) => code
        ? `<span class="bol3-ship-stop">${label} — </span><span class="bol3-ship-code">${code}</span>`
        : `<span class="bol3-ship-stop">${label}</span>`;

    if (isHandoverStyle) {
        const from = bolData.stops[0];
        const carrier = bolData.carrier;
        const width = columnWidth(2);
        const cells = [
            from ? stopCell(from, headingHtml('SHIP FROM', escapeHtml(stopCity(from))), width, true) : '',
            shipCellEle(
                '<span class="bol3-ship-name">HANDOVER CARRIER</span>',
                escapeHtml(carrier.carrierName || ''),
                contactLineHtml(carrier.driver1Name, carrier.driver1Phone),
                width
            )
        ].join('');
        return `
        <table class="bol3-block-table bol3-ship-table">
            <tbody><tr>${cells}</tr></tbody>
        </table>
    `;
    } else {
        const width = columnWidth(stopCount);
        const cells = bolData.stops.map((stop, index) =>
            stopCell(stop, headingHtml(stopHeading(index, stopCount), escapeHtml(stopCity(stop))), width)
        ).join('');
        return `
        <table class="bol3-block-table bol3-ship-table">
            <tbody><tr>${cells}</tr></tbody>
        </table>
    `;
    }
};

/** 司机展示格式：名称(联系方式)；缺一项时只展示有的那项 */
const formatDriver = (name?: string | null, phone?: string | null): string => {
    if (name && phone) return `${name}(${phone})`;
    return name || phone || '';
};

/**
 * Driver & Carrier Information 表
 */
const driverCarrierEle = (bolData: BolDataV3) => {
    const carrier = bolData.carrier;
    return `
        <div class="bol3-section-title">Driver &amp; Carrier Information</div>
        <table class="bol3-table bol3-carrier-table">
            <thead>
                <tr>
                    <!-- 两个司机列内容同构（名称(联系方式)），宽度必须相等；21.75×2 = 原 21.2+22.3，其余列不动 -->
                    <th style="width:21.75%;">Driver 1</th>
                    <th style="width:21.75%;">Driver 2</th>
                    <th style="width:13%;">Carrier</th>
                    <th style="width:13.3%;">Trailer No.</th>
                    <th style="width:12.7%;">Truck No.</th>
                    <th style="width:17.5%;">Pro Numbers</th>
                </tr>
            </thead>
            <tbody>
                <tr class="bol3-mono">
                    <td>${escapeHtml(formatDriver(carrier.driver1Name, carrier.driver1Phone))}</td>
                    <td>${escapeHtml(formatDriver(carrier.driver2Name, carrier.driver2Phone))}</td>
                    <td>${escapeHtml(carrier.carrierName)}</td>
                    <td>${escapeHtml(carrier.trailerNumber)}</td>
                    <td>${escapeHtml(carrier.licensePlate)}</td>
                    <td>${escapeHtml(carrier.proNumber || '')}</td>
                </tr>
            </tbody>
        </table>
    `;
};

/**
 * Seal Number（按路段）+ Remark
 */
const sealRemarkEle = (bolData: BolDataV3) => {
    // stops[1..] 每段一行：始发-终到 + 封签号
    const sealRows = bolData.stops.slice(1).map((stop, index) => {
        // 始发站取值规则（三方任务取上一段目的站）与编辑器共用 sealFromSortCode
        const from = sealFromSortCode(bolData.stops, bolData.taskType, index + 1);
        const to = getAbbrSortCode(stop.shipToSortCode);
        const label = to ? `${from} — ${to}` : from;
        return `
            <div class="bol3-seal-row">
                <span class="bol3-k">${escapeHtml(label)}:</span>
                <span class="bol3-v bol3-mono">${escapeHtml(stop.sealNumber)}</span>
            </div>
        `;
    }).join('');
    return `
        <div style="display:grid;grid-template-columns:1fr 1fr;">
            <span class="bol3-section-title">Seal Number</span>
            <span class="bol3-section-title">Remark</span>
        </div>
        <table class="bol3-block-table bol3-seal-table">
            <tbody>
                <tr>
                    <td class="bol3-seal-left" style="width:50%;">${sealRows}</td>
                    <td class="bol3-remark-cell" style="width:50%;">
                        <span class="bol3-remark-content">${escapeHtml(bolData.remark || '')}</span>
                    </td>
                </tr>
            </tbody>
        </table>
    `;
};

/** 容器类型 i18n key：1-卡板箱 2-蛇皮袋 3-托盘 4-Gaylord（复用任务抽屉的 containerUnit 词条） */
const CONTAINER_TYPE_LABEL_KEYS: Record<number, string> = {
    1: 'tms.task.drawer.containerUnit.palletBox',
    2: 'tms.task.drawer.containerUnit.bulkBag',
    3: 'tms.task.drawer.containerUnit.pallet',
    4: 'tms.task.drawer.containerUnit.gaylord',
};

const containerTypeLabel = (type: number): string => {
    const key = CONTAINER_TYPE_LABEL_KEYS[type];
    return key ? String(t(key)) : String(type);
};

/** 容器展示：每种类型一行 "数量/类型"，如 "10/卡板箱" */
const formatContainers = (station: StationLoadingDetail): string => {
    return (station.containers || [])
        .map(c => escapeHtml(`${c.containerCount}/${containerTypeLabel(c.containerType)}`))
        .join('<br/>');
};

/** 表体总行数：不足则补空行撑满，留白供线下手写；数据超出时按实际行数渲染，不截断 */
const LOADING_TOTAL_ROWS = 30;
/** 无到站信息时的兜底分组数 */
const FALLBACK_GROUP_COUNT = 2;

/** Loading Information 的一个 Destination 分组（rows = 数据行 + 补空行） */
interface LoadingGroup {
    stationCode: string;
    containersHtml: string;
    bags: BagDetail[];
    rows: number;
}

/**
 * 按路线到站顺序构造分组，并分配每组行数。
 * 到站以 shipToSortCode 匹配装车明细的 stationCode（内部仓两者同为仓库 sortingCode；
 * 外部站点两者同为 external_station.name——shipToSortCode 侧本就存名称，装车明细侧由后端在
 * 读路径把段码换成名称对齐，见 TmsLoadingDetailApplicationImpl），
 * 匹配不到即为空组；总行数不足 LOADING_TOTAL_ROWS 时补空行撑满：
 * 已装车的单子把空行整块追加到末尾，完全未装车的单子按均分配额逐组补
 */
const buildLoadingGroups = (stops: BolStop[], loadingDetails: StationLoadingDetail[]): LoadingGroup[] => {
    const detailMap = new Map(loadingDetails.map(detail => [detail.stationCode, detail]));
    const groups: LoadingGroup[] = stops
        .filter((stop, index) => index > 0 && (stop.shipToId || stop.shipToSortCode))
        .map((stop) => {
            const detail = detailMap.get(stop.shipToSortCode);
            detailMap.delete(stop.shipToSortCode);
            return {
                stationCode: detail?.stationCode || stop.shipToSortCode,
                containersHtml: detail ? formatContainers(detail) : '',
                bags: detail?.bags || [],
                rows: 0
            };
        });
    // 后端返回但不在路线到站中的站点（异常数据）追加到末尾，避免装车明细被丢弃
    detailMap.forEach(detail => groups.push({
        stationCode: detail.stationCode,
        containersHtml: formatContainers(detail),
        bags: detail.bags,
        rows: 0
    }));
    if (groups.length === 0) {
        for (let i = 0; i < FALLBACK_GROUP_COUNT; i += 1) {
            groups.push({ stationCode: '', containersHtml: '', bags: [], rows: 0 });
        }
    }

    // 每组至少 1 行，保证到站不消失
    groups.forEach((group) => { group.rows = Math.max(group.bags.length, 1); });
    const hasLoadedBags = groups.some(group => group.bags.length > 0);
    let deficit = LOADING_TOTAL_ROWS - groups.reduce((sum, group) => sum + group.rows, 0);
    if (deficit > 0 && hasLoadedBags) {
        // 已装车：空行统一补在所有数据行之后，不插进各到站分组内部造成中间断行；
        // 每行独立成组（rows=1），避免 Destination 等列合并成一个整块空格子而丢失格线
        for (let i = 0; i < deficit; i += 1) {
            groups.push({ stationCode: '', containersHtml: '', bags: [], rows: 1 });
        }
    } else if (deficit > 0) {
        // 完全未装车：按均分配额逐组补空行，各到站均分版面
        const base = Math.floor(LOADING_TOTAL_ROWS / groups.length);
        const remainder = LOADING_TOTAL_ROWS % groups.length;
        groups.forEach((group, i) => {
            const quota = base + (i < remainder ? 1 : 0);
            const padding = Math.min(Math.max(quota - group.rows, 0), deficit);
            group.rows += padding;
            deficit -= padding;
        });
    }
    return groups;
};

/**
 * Loading Information 表体：每组首行的 Destination / Container / Unloading 以 rowspan 合并整组，
 * ID 只对有装车数据的行连续编号，补出的空行留空（行高由 CSS 保证，见 bolBaseStyles）
 */
const buildLoadingRows = (groups: LoadingGroup[]): string => {
    let rowId = 0;
    return groups.map((group) => {
        return new Array(group.rows).fill(null).map((_, i) => {
            const bag = group.bags[i];
            const groupCells = i === 0 ? `
                <td class="bol3-dest" rowspan="${group.rows}">${escapeHtml(group.stationCode)}</td>` : '';
            const groupTailCells = i === 0 ? `
                <td class="bol3-center bol3-middle bol3-mono" rowspan="${group.rows}">${group.containersHtml}</td>
                <td rowspan="${group.rows}"></td>` : '';
            const bagCells = bag
                ? `
                    <td class="bol3-mono">${escapeHtml(bag.bagNumber)}</td>
                    <td class="bol3-center bol3-mono">${escapeHtml(String(bag.parcelCount))}</td>
                    <td class="bol3-center bol3-mono">${escapeHtml(formatWeight(bag.weight))}</td>`
                : `
                    <td class="bol3-mono"></td>
                    <td class="bol3-center bol3-mono"></td>
                    <td class="bol3-center bol3-mono"></td>`;
            return `
                <tr>
                    ${groupCells}
                    <td class="bol3-center bol3-mono">${bag ? ++rowId : ''}</td>
                    ${bagCells}
                    ${groupTailCells}
                </tr>
            `;
        }).join('');
    }).join('');
};

/**
 * Loading Information + Totals
 */
const loadingInfoEle = (bolData: BolDataV3) => {
    const details = bolData.loadingDetails || [];
    // bagNumber 为空的行是散件包裹而非包牌，不计入 Total Bags（Total Parcels 仍全部累计）
    const totalBags = details.reduce((sum, s) => sum + s.bags.filter(b => b.bagNumber && b.bagNumber.trim() !== '').length, 0);
    const totalParcels = details.reduce((sum, s) => sum + s.bags.reduce((acc, b) => acc + (b.parcelCount || 0), 0), 0);
    const totalWeight = details.reduce((sum, s) => sum + s.bags.reduce((acc, b) => acc + (b.weight ?? 0), 0), 0);
    const weightUnit = bolData.weightUnit || 'LB';
    return `
        <div class="bol3-section-title">Loading Information</div>
        <table class="bol3-table bol3-loading-table">
            <thead>
                <tr>
                    <th style="width:13.6%;">Destination</th>
                    <th class="bol3-center" style="width:6%;">ID</th>
                    <th style="width:19%;">Bagging Number</th>
                    <th class="bol3-center" style="width:13.7%;">Parcel Qty</th>
                    <th class="bol3-center" style="width:13%;">Weight</th>
                    <th class="bol3-center" style="width:17.1%;">Container</th>
                    <th class="bol3-center" style="width:17.5%;">Unloading</th>
                </tr>
            </thead>
            <tbody>
                ${buildLoadingRows(buildLoadingGroups(bolData.stops, details))}
                <tr>
                    <td class="bol3-total-cell" colspan="3">
                        <div class="bol3-total-inner">
                            <span class="bol3-total-label">Total Bags</span>
                            <span class="bol3-total-value bol3-mono">${totalBags || ''}</span>
                        </div>
                    </td>
                    <td class="bol3-total-cell" colspan="2">
                        <div class="bol3-total-inner">
                            <span class="bol3-total-label">Total Parcels</span>
                            <span class="bol3-total-value bol3-mono">${totalParcels || ''}</span>
                        </div>
                    </td>
                    <td class="bol3-total-cell" colspan="2">
                        <div class="bol3-total-inner">
                            <span class="bol3-total-label">Total Weight (${escapeHtml(weightUnit)})</span>
                            <span class="bol3-total-value bol3-mono">${totalWeight > 0 ? totalWeight.toFixed(2) : ''}</span>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    `;
};

/**
 * Signatures 表：每个站点一行
 */
const signaturesEle = (bolData: BolDataV3) => {
    // 到达/发车时间与签名均线下手写，打印时留空
    // 单站交接任务的 segment 行无到站（shipTo 全空，仅作封签/司机等数据载体），不作为签名站点渲染
    const rows = bolData.stops
        .filter((stop, index) => index === 0 || stop.shipToId || stop.shipToSortCode)
        .map((stop, index) => `
        <tr>
            <td class="bol3-loc">${escapeHtml(stopCity(stop))}</td>
            <td class="bol3-mono bol3-eta">${escapeHtml(index === 0 ? '' : formatStaticEta(stop))}</td>
            <td class="bol3-mono"></td>
            <td class="bol3-mono"></td>
            <td class="bol3-sig"></td>
            <td class="bol3-sig"></td>
        </tr>
    `).join('');
    return `
        <div class="bol3-section-title">Signatures</div>
        <table class="bol3-table bol3-sig-table">
            <thead>
                <tr>
                    <th style="width:12.5%;">Location</th>
                    <th style="width:20.5%;">Static ETA</th>
                    <th style="width:20.5%;">Arrive Time</th>
                    <th style="width:20.5%;">Departure Time</th>
                    <th style="width:13%;">Driver Sig</th>
                    <th style="width:13%;">WHS Sig</th>
                </tr>
            </thead>
            <tbody>
                ${rows}
            </tbody>
        </table>
    `;
};

/**
 * 生成 BOL v3 完整 HTML（预览与打印共用）
 */
export const generateBOLHTML = (bolData: BolDataV3) => {
    return `
        <div class="bol3-doc">
            ${headerEle()}
            ${metaEle(bolData)}
            ${shipInfoEle(bolData)}
            ${driverCarrierEle(bolData)}
            ${sealRemarkEle(bolData)}
            ${loadingInfoEle(bolData)}
            ${signaturesEle(bolData)}
        </div>
    `;
};

const BillOfLading: React.FC<{ bolData: BolDataV3 }> = ({ bolData }) => {
    return (
        <div style={{
            maxWidth: '570pt',
            margin: '0 auto',
            contain: 'layout style paint',
        }}>
            <style dangerouslySetInnerHTML={{ __html: BOL3_STYLES }} />
            <div dangerouslySetInnerHTML={{ __html: generateBOLHTML(bolData) }} />
        </div>
    );
};

export default BillOfLading;
